import * as React from 'react';
import { getSupabase } from '@/lib/supabase';
import { GoogleSignInButton } from '@/components/auth/GoogleSignInButton';
import { AuthDivider } from '@/components/auth/AuthDivider';
import { AuthShell } from '@/components/auth/AuthShell';
import { resolveAuthPortalRole } from '@/utils/resolveAuthPortalRole';
import { syncSessionToServer } from '@/utils/syncSession';
import { signOutOnServer } from '@/data/authApi';

export default function ClientSignIn() {
  React.useEffect(() => {
    try {
      localStorage.setItem('coachd:userType', 'client');
    } catch {
      // OBS-11 a-ignore: role hint for the next landing page only; a storage/setter failure just means the default route.
    }
  }, []);

  React.useEffect(() => {
    if (new URLSearchParams(window.location.search).get('portal') === 'mismatch') {
      setError('You started on the coach sign-in page. This account is a client—use client sign-in below (this page).');
    }
  }, []);

  const [mode, setMode] = React.useState<'signin' | 'forgot'>('signin');
  const [email, setEmail] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');
  const [success, setSuccess] = React.useState(false);
  const [forgotEmailSent, setForgotEmailSent] = React.useState(false);

  const panelClass = 'space-y-6 rounded-3xl border border-border bg-card p-6 shadow-xl shadow-black/5';
  const inputClass =
    'w-full rounded-xl border border-input bg-background px-4 py-3 text-foreground placeholder:text-muted-foreground focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-500/20';
  const primaryButtonClass =
    'w-full rounded-xl bg-sky-600 px-4 py-3 font-medium text-white transition-colors hover:bg-sky-500 disabled:cursor-not-allowed disabled:opacity-50';
  const legalFooter = (
    <p className="text-center text-xs text-muted-foreground">
      By continuing you agree to our{' '}
      <a
        href="https://os-coach.com/terms"
        target="_blank"
        rel="noopener noreferrer"
        className="font-medium text-foreground underline underline-offset-2"
      >
        Terms of Service
      </a>{' '}
      and{' '}
      <a
        href="https://os-coach.com/privacy"
        target="_blank"
        rel="noopener noreferrer"
        className="font-medium text-foreground underline underline-offset-2"
      >
        Privacy Policy
      </a>
      .
    </p>
  );

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    const sb = getSupabase();
    if (!sb) {
      setError('Auth not configured');
      return;
    }
    if (!email.trim()) {
      setError('Email is required');
      return;
    }
    setLoading(true);
    try {
      // See CoachAuth forgot-password: RedirectTo must have no `?` because
      // Supabase's recovery email appends `?token_hash=...&type=recovery`.
      const redirectTo = `${window.location.origin}/auth/callback`;
      const { error: resetError } = await sb.auth.resetPasswordForEmail(email.trim(), { redirectTo });
      if (resetError) throw resetError;
      setForgotEmailSent(true);
    } catch (e: any) {
      setError(e?.message || 'Failed to send reset email. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    const sb = getSupabase();
    if (!sb) {
      setError('Auth not configured');
      return;
    }
    setLoading(true);
    setError('');
    try {
      // Redirect to Worker-initiated OAuth flow
      const params = new URLSearchParams({ provider: 'google', auth_portal: 'client' });
      window.location.href = `/api/auth/start?${params.toString()}`;
    } catch (e: any) {
      setError(e?.message || 'Failed to sign in with Google');
      setLoading(false);
    }
  };

  const handlePasswordSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    const sb = getSupabase();
    if (!sb) {
      setError('Auth not configured');
      return;
    }
    if (!email.trim() || !password) {
      setError('Email and password are required');
      return;
    }
    setLoading(true);
    try {
      const { data, error } = await sb.auth.signInWithPassword({ email: email.trim(), password });
      if (error) throw error;

      // Sync session to server-side cookies so Worker endpoints authenticate.
      // Await completion BEFORE redirect to avoid a cookie race on slow networks.
      if (data.session) {
        const synced = await syncSessionToServer({
          access_token: data.session.access_token,
          refresh_token: data.session.refresh_token,
        });
        if (!synced) {
          setError('Sign-in succeeded but we could not sync your session. Please try again.');
          return;
        }
      }

      if (data.user) {
        const role = await resolveAuthPortalRole(sb, data.user);
        if (role === 'coach') {
          await signOutOnServer();
          await sb.auth.signOut();
          setError('This account is a coach. Use coach sign-in below.');
          return;
        }
      }

      setSuccess(true);
      localStorage.setItem('coachd:userType', 'client');
      const returnTo = new URLSearchParams(window.location.search).get('returnTo');
      setTimeout(() => window.location.replace(returnTo || '/?wm=calendar'), 800);
    } catch (e: any) {
      setError(e?.message || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell
      role="client"
      title={mode === 'forgot' ? 'Reset your client password' : 'Client Sign In'}
      subtitle={
        mode === 'forgot'
          ? "We'll email you a link to reset your password."
          : 'Access your workouts, nutrition plans, messages, and health data.'
      }
      footer={legalFooter}
    >
      {success ? (
        <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-4 text-center">
          <p className="text-green-400">Success! Redirecting…</p>
        </div>
      ) : mode === 'forgot' ? (
        <div className={panelClass}>
          {error && (
            <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3 text-sm text-red-400">{error}</div>
          )}

          {forgotEmailSent ? (
            <div className="space-y-4 text-center">
              <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-sky-500/10">
                <svg
                  className="h-6 w-6 text-sky-600 dark:text-sky-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                  />
                </svg>
              </div>
              <div>
                <p className="text-sm text-foreground">Check your email</p>
                <p className="mt-1 text-sm text-muted-foreground">
                  If an account exists for <span className="font-medium text-foreground">{email}</span>, we sent a reset
                  link. The link is valid for one hour.
                </p>
              </div>
              <button
                type="button"
                onClick={() => {
                  setMode('signin');
                  setForgotEmailSent(false);
                  setError('');
                }}
                className="text-sm font-medium text-sky-600 hover:underline dark:text-sky-400"
              >
                Back to sign in
              </button>
            </div>
          ) : (
            <form onSubmit={handleForgotPassword} className="space-y-4">
              <div>
                <label htmlFor="forgot-email" className="block text-sm font-medium mb-2">
                  Email
                </label>
                <input
                  id="forgot-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className={inputClass}
                  placeholder="you@example.com"
                  required
                  disabled={loading}
                />
              </div>
              <button type="submit" disabled={loading || !email.trim()} className={primaryButtonClass}>
                {loading ? 'Sending…' : 'Send reset link'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setMode('signin');
                  setError('');
                }}
                className="w-full text-center text-sm text-muted-foreground transition-colors hover:text-foreground"
              >
                Back to sign in
              </button>
            </form>
          )}
        </div>
      ) : (
        <div className={panelClass}>
          {error && (
            <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3 text-sm text-red-400">{error}</div>
          )}

          <GoogleSignInButton onSignIn={handleGoogleSignIn} loading={loading} />

          <AuthDivider />

          <form onSubmit={handlePasswordSignIn} className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium mb-2">
                Email
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={inputClass}
                placeholder="you@example.com"
                required
                disabled={loading}
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <label htmlFor="password" className="block text-sm font-medium">
                  Password
                </label>
                <button
                  type="button"
                  onClick={() => {
                    setMode('forgot');
                    setError('');
                    setPassword('');
                  }}
                  className="text-xs font-medium text-sky-600 hover:underline dark:text-sky-400"
                >
                  Forgot password?
                </button>
              </div>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className={inputClass}
                placeholder="Your password"
                required
                disabled={loading}
              />
            </div>

            <button type="submit" disabled={loading || !email.trim() || !password} className={primaryButtonClass}>
              {loading ? 'Please wait…' : 'Sign In'}
            </button>
          </form>

          <p className="rounded-2xl border border-border bg-muted/40 px-4 py-3 text-center text-sm text-muted-foreground">
            Need access? Ask your coach to send you an invite link. Client accounts can only be created from a coach
            invitation.
          </p>

          <p className="border-t border-border pt-2 text-center text-sm text-muted-foreground">
            Are you a coach?{' '}
            <a href="/coach/sign-in" className="font-medium text-sky-600 hover:underline dark:text-sky-400">
              Coach sign in
            </a>
          </p>
        </div>
      )}
    </AuthShell>
  );
}
